import {t} from '../localization/adminText';
import InspectorPanel from './CodFormDesigner/InspectorPanel';
import { clampNumber, fieldStyleFromGlobal } from './CodFormDesigner/style';
import { Toggle, ColorSetting, NumberSetting } from './CodFormDesigner/controls';
import { FieldIconGraphic, PreviewField, PreviewProductInformation } from './CodFormDesigner/preview';
import { useEffect, useId, useMemo, useRef, useState } from "react";

import CodFieldIcon, { defaultFieldIconName } from "./CodFieldIcon";
import "./CodFormDesigner.css";

const CORE_REQUIRED_FIELDS = new Set(["phone", "address1", "city", "country_code"]);

const AUTOCOMPLETE_OPTIONS = [
  ["off", "Off"],
  ["given-name", "Given name"],
  ["family-name", "Family name"],
  ["email", "Email"],
  ["tel", "Phone"],
  ["address-line1", "Address line 1"],
  ["address-line2", "Address line 2"],
  ["address-level1", "State / Province"],
  ["address-level2", "City"],
  ["country", "Country"],
  ["postal-code", "Postal code"],
];

export default function CodFormDesigner({ settings, setSettings, onSave, onReset, onChange, saving, loading, saveVersion = 0, translationEditor = null, contentLocale, dirty: externalDirty }) {
  const [device, setDevice] = useState("desktop");
  const [mode, setMode] = useState("edit");
  const [panelTab, setPanelTab] = useState("content");
  const [selectedField, setSelectedField] = useState("");
  const [draggedField, setDraggedField] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [localDirty, setDirty] = useState(false);
  const dirty = externalDirty ?? localDirty;
  const [inspectorRequest, setInspectorRequest] = useState(0);
  const [popupOpen, setPopupOpen] = useState(true);
  const popupLaunchRef = useRef(null);
  const popupCloseRef = useRef(null);
  const inspectorId = useId();

  useEffect(() => {
    setDirty(false);
  }, [saveVersion]);

  const fields = useMemo(() => settings.form_schema || [], [settings.form_schema]);
  const style = settings.style || {};
  const popupPreview = mode === "preview" && style.display_mode === "popup";

  useEffect(() => {
    if (popupPreview && popupOpen) popupCloseRef.current?.focus();
  }, [popupPreview, popupOpen]);

  const closePopupPreview = () => {
    setPopupOpen(false);
    popupLaunchRef.current?.focus();
  };
  const configuredCountries = settings.countries || [];
  const singleCountryCode = configuredCountries.length === 1 ? configuredCountries[0] : "";
  const firstCountryCode = configuredCountries[0] || "";
  const previewCountry = firstCountryCode
    ? (settings.country_catalog || []).find((country) => country.country_code === firstCountryCode)
    : null;
  const provinceRule = previewCountry?.address_rules?.province || {};
  const provinceSupported = !firstCountryCode
    || (provinceRule.supported !== false && provinceRule.input !== "hidden");
  const previewFields = mode === "preview"
    ? fields.filter((field) => {
        if (field.name === "country_code" && singleCountryCode) return false;
        if (field.name === "province_code" && !provinceSupported) return false;
        return true;
      })
    : fields;
  const selected = fields.find((field) => field.name === selectedField) || null;
  const selectedProductInformation = selected?.name === "product_information";
  const selectedFieldStyle = {
    ...fieldStyleFromGlobal(style),
    ...(selected?.style || {}),
  };
  const fieldMap = useMemo(() => new Set(fields.map((field) => field.name)), [fields]);
  const hiddenFields = useMemo(() => {
    const query = search.trim().toLowerCase();
    return (settings.available_fields || []).filter((field) => {
      if (fieldMap.has(field.name)) return false;
      return !query || field.label.toLowerCase().includes(query) || field.name.includes(query);
    });
  }, [fieldMap, search, settings.available_fields]);

  useEffect(() => {
    if (selectedField && !fields.some((field) => field.name === selectedField)) {
      setSelectedField("");
      setPanelTab("content");
    }
  }, [fields, selectedField]);

  const selectForm = () => {
    setInspectorRequest(value => value + 1);
    setSelectedField("");
    setPanelTab("content");
  };

  const selectField = (name) => {
    setInspectorRequest(value => value + 1);
    setSelectedField(name);
    setPanelTab("content");
  };

  const changeSettings = (updater) => {
    if (loading || translationEditor) return;
    setSettings((current) => (typeof updater === "function" ? updater(current) : updater));
    setDirty(true);
    onChange?.();
  };

  const patchStyle = (key, value) => {
    changeSettings((current) => ({
      ...current,
      style: { ...current.style, [key]: value },
    }));
  };

  const patchField = (name, patch) => {
    changeSettings((current) => ({
      ...current,
      form_schema: current.form_schema.map((field) =>
        field.name === name ? { ...field, ...patch } : field,
      ),
    }));
  };

  const patchFieldStyle = (name, key, value) => {
    changeSettings((current) => ({
      ...current,
      form_schema: current.form_schema.map((field) =>
        field.name === name
          ? { ...field, style: { ...(field.style || {}), [key]: value } }
          : field,
      ),
    }));
  };

  const setLabelMode = (modeName) => {
    if (!selected) return;
    const patch = modeName === "hidden"
      ? { show_label: false, floating_label: false, show_placeholder: true }
      : modeName === "floating"
        ? { show_label: true, floating_label: true, show_placeholder: true }
        : { show_label: true, floating_label: false, show_placeholder: true };
    patchField(selected.name, patch);
  };

  const moveField = (sourceName, targetName) => {
    if (!sourceName || sourceName === targetName) return;
    changeSettings((current) => {
      const next = [...current.form_schema];
      const sourceIndex = next.findIndex((field) => field.name === sourceName);
      const targetIndex = next.findIndex((field) => field.name === targetName);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return { ...current, form_schema: next };
    });
  };

  const addField = (definition) => {
    changeSettings((current) => {
      const next = [...current.form_schema];
      if (next.some((field) => field.name === definition.name)) return current;
      next.push({ ...definition });
      return { ...current, form_schema: next };
    });
    selectField(definition.name);
    setAddOpen(false);
  };

  const removeField = (name) => {
    if (CORE_REQUIRED_FIELDS.has(name)) return;
    const nextFields = fields.filter((field) => field.name !== name);
    changeSettings((current) => ({ ...current, form_schema: nextFields }));
    if (selectedField === name) selectForm();
  };

  const formVars = {
    "--cfd-input-text": style.text_color,
    "--cfd-label": style.label_color,
    "--cfd-form-bg": style.form_background_color,
    "--cfd-form-border": style.form_border_color,
    "--cfd-form-border-width": `${style.form_border_width}px`,
    "--cfd-form-radius": `${style.form_border_radius}px`,
    "--cfd-form-padding": `${style.form_padding}px`,
    "--cfd-field-gap": `${style.field_gap}px`,
    "--cfd-button-bg": style.submit_button_background_color,
    "--cfd-button-color": style.submit_button_text_color,
    "--cfd-order-total-text-color": style.order_total_text_color || "#333333",
    "--cfd-order-total-background-color": style.order_total_background_color || "#ffffff",
    "--cfd-order-total-font-size": `${style.order_total_font_size ?? 20}px`,
    "--cfd-order-total-padding": `${style.order_total_padding ?? 14}px`,
    "--cfd-order-total-border-radius": `${style.order_total_border_radius ?? 0}px`,
    "--cfd-submit-button-active-background-color": style.submit_button_active_background_color || "#374151",
    "--cfd-submit-button-font-size": `${style.submit_button_font_size ?? 16}px`,
    "--cfd-submit-button-height": `${style.submit_button_height ?? 48}px`,
    "--cfd-submit-button-border-radius": `${style.submit_button_border_radius ?? 8}px`,
    "--cfd-form-max-width": `${style.form_max_width || 760}px`,
  };

  const labelMode = !selected?.show_label
    ? "hidden"
    : selected?.floating_label
      ? "floating"
      : "fixed";
  const selectedIconMode = selected?.icon_mode || "default";
  const selectedIconName = selected?.icon_name || defaultFieldIconName(selected?.name);
  const countryShipping = (settings.shipping_options || []).filter(option => !option.country_code || option.country_code === firstCountryCode);
  const defaultShipping = countryShipping.find(option => option.is_default) || countryShipping[0];
  const previewCurrency = defaultShipping?.currency_code || settings.currency || 'USD';
  const previewPrecision = defaultShipping?.currency_code ? (previewCountry?.decimal_digits ?? 2) : (settings.decimal_digits ?? 2);

  const formPreview = (
            <div
              className={`cfd-form-preview is-${style.field_layout || "auto"} is-width-${style.form_width || "theme"}${style.mobile_single_column ? " mobile-single" : ""}${popupPreview ? " is-popup" : ""}${popupPreview && style.popup_sticky_footer !== false ? " has-sticky-footer" : ""}`}
              style={formVars}
              lang={contentLocale} dir={contentLocale?.startsWith("ar") ? "rtl" : undefined}
            >
              {popupPreview ? <button className="cfd-popup-close" type="button" ref={popupCloseRef} aria-label="Close COD form preview" onClick={closePopupPreview}>×</button> : null}
              <div className="cfd-form-heading" hidden={!style.form_title && !style.form_subtitle}>
                {style.form_title ? <h2>{style.form_title}</h2> : null}
                {style.form_subtitle ? <p>{style.form_subtitle}</p> : null}
              </div>
              <div className="cfd-form-scroll">
              <div className="cfd-fields-grid">
                {previewFields.map((field) => {
                  const FieldPreview = field.name === "product_information" ? PreviewProductInformation : PreviewField;
                  return <FieldPreview
                    key={field.name}
                    field={field}
                    selected={mode === "edit" && selected?.name === field.name}
                    editing={mode === "edit" && !translationEditor}
                    onSelect={selectField}
                    globalStyle={style}
                    currency={previewCurrency}
                    decimalDigits={previewPrecision}
                    sampleValue={field.name === "country_code"
                      ? previewCountry?.country_name || firstCountryCode || ""
                      : undefined}
                    dragHandlers={{
                      onDragStart: () => setDraggedField(field.name),
                      onDragEnd: () => setDraggedField(""),
                      onDragOver: (event) => event.preventDefault(),
                      onDrop: () => {
                        moveField(draggedField, field.name);
                        setDraggedField("");
                      },
                    }}
                  />;
                })}
              </div>

              {style.show_shipping_method && (mode === "edit" || defaultShipping) ? (
                <div className="cfd-shipping-card" onClick={() => mode === "edit" && selectForm()}>
                  <div className="cfd-component-heading">
                    <strong>Shipping method <span>(optional component)</span></strong>
                    {mode === "edit" ? (
                      <button type="button" onClick={(event) => { event.stopPropagation(); patchStyle("show_shipping_method", false); }}>Remove</button>
                    ) : null}
                  </div>
                  <div className="cfd-shipping-option">
                    <i />
                    <span><strong>{defaultShipping?.name || "Standard shipping"}</strong><small>{t("Example shipping option")}</small></span>
                    <b>{Number(defaultShipping?.value || 0) === 0 ? "Free" : `${defaultShipping.value} ${previewCurrency}`}</b>
                  </div>
                </div>
              ) : null}

              </div>
              <div className="cfd-form-footer">
              {style.show_order_total ? (
                <div className="cfd-total-row">
                  <strong>{style.order_total_label ?? "Total"}</strong>
                  <span><b>{(99 + Number(defaultShipping?.value || 0)).toFixed(previewPrecision)} {previewCurrency}</b>{style.show_cod_note && (style.cod_note_label ?? "Cash on delivery (COD)") ? <small>{style.cod_note_label ?? "Cash on delivery (COD)"}</small> : null}</span>
                </div>
              ) : null}
              <button
                className={`cfd-submit${style.submit_button_full_width ? " is-full" : ""}`}
                type="button"
                disabled={mode === "preview"}
              >
                {style.submit_button_label || "Place COD order"}
              </button>
              </div>
            </div>
  );

  return (
    <section className="cfd-shell">
      <header className="cfd-toolbar">
        <span className={`cfd-save-status${dirty ? " is-dirty" : ""}`}>
          <i /> {t(loading ? "Loading…" : saving ? "Saving…" : dirty ? "Unsaved changes" : "Saved")}
        </span>
        <div className="cfd-segmented" aria-label="Preview device">
          <button className={device === "desktop" ? "is-active" : ""} onClick={() => setDevice("desktop")} type="button">Desktop</button>
          <button className={device === "mobile" ? "is-active" : ""} onClick={() => setDevice("mobile")} type="button">Mobile</button>
        </div>
        <div className="cfd-toolbar-actions">
          {mode === "edit" ? (
            <button type="button" disabled={loading} onClick={selectForm}>{t("Form settings")}</button>
          ) : null}
          {mode === "edit" ? (
            <button type="button" disabled={loading} onClick={() => setAddOpen(true)}><b>＋</b> Add field</button>
          ) : null}
          <button className={mode === "edit" ? "is-active" : ""} type="button" onClick={() => setMode("edit")}>✎ Edit mode</button>
          <button className={mode === "preview" ? "is-active" : ""} type="button" onClick={() => { setMode("preview"); setPopupOpen(true); }}>◉ Preview mode</button>
        </div>
      </header>

      {mode === "preview" ? (
        <div className="cfd-preview-banner">Preview mode: editing and submission are disabled. Product details and prices are examples; your storefront uses the current order.</div>
      ) : null}

      {mode === "edit" && <p className="cfd-sample-note">{t("Sample product and prices. Select a field to edit its settings.")}</p>}
      <div className={`cfd-workspace${mode === "preview" ? " is-preview" : ""}`}>
        <main className="cfd-canvas">
          <div className={`cfd-device-frame is-${device}`}>
            {popupPreview ? (
              <div className="cfd-popup-stage" style={{
                "--cfd-popup-max-width": String(style.popup_max_width ?? 760) + "px",
                "--cfd-popup-radius": String(style.popup_border_radius ?? 16) + "px",
                "--cfd-popup-overlay": "rgba(0, 0, 0, " + ((style.popup_overlay_opacity ?? 40) / 100) + ")",
              }}>
                <div className="cfd-popup-page-placeholder" aria-hidden={popupOpen}>
                  <span className="cfd-sample-badge">Sample product page</span>
                  <div className="cfd-popup-page-image" aria-hidden="true">Product image</div>
                  <h2>Sample product</h2>
                  <p>Open the form to preview your delivery fields and order components.</p>
                  <button
                    type="button"
                    ref={popupLaunchRef}
                    className={"cfd-popup-launch" + (style.popup_button_full_width !== false ? " is-full" : "")}
                    style={{ backgroundColor: style.popup_button_background_color || "#111827", color: style.popup_button_text_color || "#ffffff" }}
                    tabIndex={popupOpen ? -1 : 0}
                    onClick={() => setPopupOpen(true)}
                  >{style.popup_button_label || "Order with cash on delivery"}</button>
                </div>
                {popupOpen ? (
                  <div className="cfd-popup-overlay">
                    <div className="cfd-popup-dialog" role="dialog" aria-label="COD form popup preview" onKeyDown={(event) => {
                      if (event.key === "Escape") { event.stopPropagation(); closePopupPreview(); }
                    }}>
                      {formPreview}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : mode === "edit" ? (
              <div className={`cfd-form-editor-frame is-width-${style.form_width || "theme"}${!selected ? " is-selected" : ""}`} style={formVars} data-testid="form-editor-frame">
                {formPreview}
                <button
                  className="cfd-form-edit-button"
                  type="button"
                  aria-label="Edit form settings"
                  aria-pressed={!selected}
                  title="Edit form settings"
                  onClick={selectForm}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m15 5 4 4M4 20l4.5-1 11-11a2.8 2.8 0 0 0-4-4l-11 11L4 20Z" />
                  </svg>
                </button>
              </div>
            ) : formPreview}
          </div>
        </main>

        {translationEditor ? translationEditor : mode === "edit" ? (
          <InspectorPanel request={inspectorRequest}><aside className="cfd-inspector" aria-label="Form designer settings">
            <div className="cfd-inspector-title">
              <span className="cfd-grip">⋮⋮</span>
              <h2>{selected?.label || "Form settings"}</h2>
            </div>
            <nav className="cfd-inspector-tabs" role="tablist" aria-label={selected ? "Field settings tabs" : "Form settings tabs"} onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const nextTab = event.key === "Home" ? "content" : event.key === "End" ? "style" : panelTab === "content" ? "style" : "content";
              setPanelTab(nextTab);
              event.currentTarget.querySelector(`[data-tab="${nextTab}"]`)?.focus();
            }}>
              <button id={`${inspectorId}-content`} data-tab="content" role="tab" aria-selected={panelTab === "content"} aria-controls={`${inspectorId}-panel`} tabIndex={panelTab === "content" ? 0 : -1} className={panelTab === "content" ? "is-active" : ""} onClick={() => setPanelTab("content")} type="button">{selected ? "Field settings" : "Content settings"}</button>
              <button id={`${inspectorId}-style`} data-tab="style" role="tab" aria-selected={panelTab === "style"} aria-controls={`${inspectorId}-panel`} tabIndex={panelTab === "style" ? 0 : -1} className={panelTab === "style" ? "is-active" : ""} onClick={() => setPanelTab("style")} type="button">Style settings</button>
            </nav>

            <div key={`${selectedField}-${panelTab}`} className="cfd-inspector-body" id={`${inspectorId}-panel`} role="tabpanel" aria-labelledby={`${inspectorId}-${panelTab}`} tabIndex={0}>
              {panelTab === "content" && selectedProductInformation ? (
                <div className="cfd-settings-stack">
                  <div className="cfd-empty-note">Shows the products in this COD order, using the current image, variant, quantity and price. This preview uses a sample product.</div>
                  <Toggle label="Show product image" checked={selected.show_image !== false} onChange={(value) => patchField(selected.name, { show_image: value })} />
                  <Toggle label="Show variant" checked={selected.show_variant !== false} onChange={(value) => patchField(selected.name, { show_variant: value })} />
                  <Toggle label="Show quantity" checked={selected.show_quantity !== false} onChange={(value) => patchField(selected.name, { show_quantity: value })} />
                  <Toggle label="Show product price" checked={selected.show_price !== false} onChange={(value) => patchField(selected.name, { show_price: value })} />
                  <p className="cfd-setting-help">Drag this component in the form to change its position. Remove it to hide the summary; the products in the order stay the same. Add it again with Add field → Order components.</p>
                  <button className="cfd-danger-link" type="button" onClick={() => removeField(selected.name)}>Remove product information</button>
                </div>
              ) : null}

              {panelTab === "content" && selected && !selectedProductInformation ? (
                <div className="cfd-settings-stack">
                  <label className="cfd-control-group">Label text<input value={selected.label} maxLength={100} onChange={(event) => patchField(selected.name, { label: event.target.value })} /></label>
                  <label className="cfd-control-group">Placeholder<input value={selected.placeholder || ""} maxLength={100} onChange={(event) => patchField(selected.name, { placeholder: event.target.value })} /></label>
                  <fieldset className="cfd-radio-group">
                    <legend>Label display</legend>
                    {[['fixed', 'Fixed at upper-left'], ['floating', 'Float after input'], ['hidden', 'Hide label']].map(([value, label]) => (
                      <label className={labelMode === value ? "is-active" : ""} key={value}>
                        <input type="radio" checked={labelMode === value} onChange={() => setLabelMode(value)} />{label}
                      </label>
                    ))}
                  </fieldset>
                  <Toggle
                    label="Required field"
                    checked={CORE_REQUIRED_FIELDS.has(selected.name) || selected.required}
                    disabled={CORE_REQUIRED_FIELDS.has(selected.name) || ["province_code", "discount_code"].includes(selected.name)}
                    onChange={(value) => patchField(selected.name, { required: value })}
                  />
                  <label className="cfd-control-group">Required message<input value={selected.required_message || ""} maxLength={160} onChange={(event) => patchField(selected.name, { required_message: event.target.value })} /></label>
                  <label className="cfd-control-group">Browser autocomplete
                    <select value={selected.autocomplete || "off"} onChange={(event) => patchField(selected.name, { autocomplete: event.target.value })}>
                      {AUTOCOMPLETE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </label>
                  <h3>Left icon</h3>
                  <fieldset className="cfd-choice-group three">
                    <legend>Icon source</legend>
                    {[["default", "Default icon"], ["custom", "Image URL"], ["hidden", "Hide icon"]].map(([value, label]) => (
                      <button
                        type="button"
                        key={value}
                        className={selectedIconMode === value ? "is-active" : ""}
                        onClick={() => patchField(selected.name, { icon_mode: value })}
                      >
                        {label}
                      </button>
                    ))}
                  </fieldset>
                  {selectedIconMode === "custom" ? (
                    <>
                      <label className="cfd-control-group">
                        Custom image URL
                        <input
                          type="url"
                          aria-label="Custom image URL"
                          value={selected.icon_url || ""}
                          maxLength={2048}
                          placeholder="https://example.com/icon.svg"
                          onChange={(event) => patchField(selected.name, { icon_url: event.target.value })}
                        />
                        <small>SVG, PNG or WebP · Recommended 24 × 24 px</small>
                      </label>
                      <div className="cfd-icon-preview-row">
                        <span className="cfd-icon-preview-tile"><FieldIconGraphic field={selected} /></span>
                        <span><strong>Default fallback</strong><small>Used when the image cannot load</small></span>
                        <button type="button" onClick={() => patchField(selected.name, { icon_mode: "default", icon_url: "", icon_size: 20 })}>Restore default</button>
                      </div>
                    </>
                  ) : selectedIconMode === "default" ? (
                    <div className="cfd-icon-preview-row">
                      <span className="cfd-icon-preview-tile"><CodFieldIcon name={selectedIconName} /></span>
                      <span><strong>Built-in icon</strong><small>{selectedIconName}.svg</small></span>
                      <button type="button" onClick={() => patchField(selected.name, { icon_mode: "default", icon_url: "", icon_size: 20 })}>Restore default</button>
                    </div>
                  ) : null}
                  {selectedIconMode !== "hidden" ? (
                    <NumberSetting label="Icon size" value={selected.icon_size ?? 20} min={12} max={32} onChange={(value) => patchField(selected.name, { icon_size: clampNumber(value, 12, 32) })} />
                  ) : null}
                  <fieldset className="cfd-choice-group">
                    <legend>Field width</legend>
                    <button type="button" className={selected.width === "full" ? "is-active" : ""} onClick={() => patchField(selected.name, { width: "full" })}>Full row</button>
                    <button type="button" className={selected.width !== "full" ? "is-active" : ""} onClick={() => patchField(selected.name, { width: "half" })}>Half row</button>
                  </fieldset>
                  <button className="cfd-danger-link" type="button" disabled={CORE_REQUIRED_FIELDS.has(selected.name)} onClick={() => removeField(selected.name)}>
                    {CORE_REQUIRED_FIELDS.has(selected.name) ? "This core field cannot be hidden" : "Hide this field"}
                  </button>
                </div>
              ) : null}

              {panelTab === "style" && selectedProductInformation ? <div className="cfd-empty-note">Product information uses the form's colors and spacing. Choose Field settings to show or hide its image, variant, quantity and price.</div> : null}
              {panelTab === "style" && selected && !selectedProductInformation ? (
                <div className="cfd-settings-stack">
                  <Toggle
                    label="Use global style"
                    description="Turn this off to style only this field."
                    checked={selected.use_global_style !== false}
                    onChange={(value) => patchField(selected.name, {
                      use_global_style: value,
                      style: selected.style || fieldStyleFromGlobal(style),
                    })}
                  />
                  {selected.use_global_style === false ? (
                    <>
                      <NumberSetting label="Input height" value={selectedFieldStyle.input_height} min={40} max={80} onChange={(value) => patchFieldStyle(selected.name, "input_height", clampNumber(value, 40, 80))} />
                      <ColorSetting label="Input background" value={selectedFieldStyle.input_background_color} onChange={(value) => patchFieldStyle(selected.name, "input_background_color", value)} />
                      <ColorSetting label="Input border" value={selectedFieldStyle.input_border_color} onChange={(value) => patchFieldStyle(selected.name, "input_border_color", value)} />
                      <NumberSetting label="Border width" value={selectedFieldStyle.input_border_width} min={0} max={4} onChange={(value) => patchFieldStyle(selected.name, "input_border_width", clampNumber(value, 0, 4))} />
                      <ColorSetting label="Focus border" value={selectedFieldStyle.input_focus_border_color} onChange={(value) => patchFieldStyle(selected.name, "input_focus_border_color", value)} />
                      <ColorSetting label="Input text" value={selectedFieldStyle.text_color} onChange={(value) => patchFieldStyle(selected.name, "text_color", value)} />
                      <ColorSetting label="Label" value={selectedFieldStyle.label_color} onChange={(value) => patchFieldStyle(selected.name, "label_color", value)} />
                      <ColorSetting label="Placeholder" value={selectedFieldStyle.placeholder_color} onChange={(value) => patchFieldStyle(selected.name, "placeholder_color", value)} />
                      <NumberSetting label="Corner radius" value={selectedFieldStyle.input_border_radius} min={0} max={30} onChange={(value) => patchFieldStyle(selected.name, "input_border_radius", clampNumber(value, 0, 30))} />
                      <ColorSetting label="Error state" value={selectedFieldStyle.input_error_color} onChange={(value) => patchFieldStyle(selected.name, "input_error_color", value)} />
                    </>
                  ) : (
                    <div className="cfd-empty-note">This field inherits all colors, dimensions and error styling from Form settings. Use the form's corner edit button, then Style settings, to change these defaults.</div>
                  )}
                </div>
              ) : null}

              {panelTab === "content" && !selected ? (
                <div className="cfd-settings-stack">
                  <h3>General</h3>
                  <fieldset className="cfd-choice-group">
                    <legend>Display mode</legend>
                    <button type="button" aria-pressed={style.display_mode !== "popup"} className={style.display_mode !== "popup" ? "is-active" : ""} onClick={() => patchStyle("display_mode", "embedded")}>Embedded form</button>
                    <button type="button" aria-pressed={style.display_mode === "popup"} className={style.display_mode === "popup" ? "is-active" : ""} onClick={() => patchStyle("display_mode", "popup")}>Popup form</button>
                  </fieldset>
                  <p className="cfd-setting-help">Both display modes use the same fields, styles and Upsell settings. Keep dragging and editing this form as usual; Preview mode shows how it opens on the storefront.</p>
                  {style.display_mode === "popup" ? (
                    <>
                      <div className="cfd-empty-note">The storefront button only opens the form. An order is created when the customer submits the completed form inside the popup.</div>
                      <label className="cfd-control-group">Open-form button text<input value={style.popup_button_label ?? "Order with cash on delivery"} maxLength={100} onChange={(event) => patchStyle("popup_button_label", event.target.value)} /></label>
                      <Toggle label="Keep total and submit button visible" description="Long forms scroll inside the popup. The total and submit button stay at the bottom." checked={style.popup_sticky_footer !== false} onChange={(value) => patchStyle("popup_sticky_footer", value)} />
                      <p className="cfd-setting-help">The popup fits the available screen width. Fields adapt to the form's own width. On mobile, the form opens close to full screen.</p>
                    </>
                  ) : <p className="cfd-setting-help">Show the form directly in the page at its existing position.</p>}
                  <h3>Form content</h3>
                  <label className="cfd-control-group">Form title<input value={style.form_title || ""} maxLength={100} onChange={(event) => patchStyle("form_title", event.target.value)} /></label>
                  <label className="cfd-control-group">Form subtitle<input value={style.form_subtitle || ""} maxLength={180} onChange={(event) => patchStyle("form_subtitle", event.target.value)} /></label>
                  <label className="cfd-control-group">Button text<input value={style.submit_button_label || ""} maxLength={100} onChange={(event) => patchStyle("submit_button_label", event.target.value)} /></label>
                  <h3>Order summary</h3>
                  <Toggle label="Show shipping method" description="When hidden, the default COD shipping rate is applied automatically." checked={style.show_shipping_method} onChange={(value) => patchStyle("show_shipping_method", value)} />
                  <Toggle label="Show order total" checked={style.show_order_total} onChange={(value) => patchStyle("show_order_total", value)} />
                  <Toggle label="Show COD note" checked={style.show_cod_note} disabled={!style.show_order_total} onChange={(value) => patchStyle("show_cod_note", value)} />
                  <label className="cfd-control-group">Total label<input value={style.order_total_label ?? "Total"} maxLength={100} onChange={(event) => patchStyle("order_total_label", event.target.value)} /></label>
                  <label className="cfd-control-group">COD note text<input value={style.cod_note_label ?? "Cash on delivery (COD)"} maxLength={100} onChange={(event) => patchStyle("cod_note_label", event.target.value)} /></label>
                </div>
              ) : null}

              {panelTab === "style" && !selected ? (
                <div className="cfd-settings-stack">
                  <h3>Layout</h3>
                  <fieldset className="cfd-choice-group three">
                    <legend>Form width</legend>
                    {[["theme", "Theme"], ["full", "Full width"], ["custom", "Custom"]].map(([value, label]) => (
                      <button type="button" key={value} className={style.form_width === value ? "is-active" : ""} onClick={() => patchStyle("form_width", value)}>{label}</button>
                    ))}
                  </fieldset>
                  {style.form_width === "custom" ? <NumberSetting label="Maximum width" value={style.form_max_width} min={320} max={1600} onChange={(value) => patchStyle("form_max_width", clampNumber(value, 320, 1600))} /> : null}
                  <fieldset className="cfd-choice-group">
                    <legend>Field layout</legend>
                    <button type="button" className={style.field_layout === "single" ? "is-active" : ""} onClick={() => patchStyle("field_layout", "single")}>Single column</button>
                    <button type="button" className={style.field_layout !== "single" ? "is-active" : ""} onClick={() => patchStyle("field_layout", "auto")}>Auto columns</button>
                  </fieldset>
                  <Toggle label="Single column on mobile" checked={style.mobile_single_column} onChange={(value) => patchStyle("mobile_single_column", value)} />
                  <NumberSetting label="Field gap" value={style.field_gap} min={0} max={40} onChange={(value) => patchStyle("field_gap", clampNumber(value, 0, 40))} />
                  <NumberSetting label="Form padding" value={style.form_padding} min={0} max={64} onChange={(value) => patchStyle("form_padding", clampNumber(value, 0, 64))} />
                  <h3>Order total</h3>
                  <ColorSetting label="Total text color" value={style.order_total_text_color || "#333333"} onChange={(value) => patchStyle("order_total_text_color", value)} />
                  <ColorSetting label="Total background" value={style.order_total_background_color || "#ffffff"} onChange={(value) => patchStyle("order_total_background_color", value)} />
                  <NumberSetting label="Total font size" value={style.order_total_font_size ?? 20} min={12} max={40} onChange={(value) => patchStyle("order_total_font_size", clampNumber(value, 12, 40))} />
                  <NumberSetting label="Total padding" value={style.order_total_padding ?? 14} min={0} max={40} onChange={(value) => patchStyle("order_total_padding", clampNumber(value, 0, 40))} />
                  <NumberSetting label="Total corner radius" value={style.order_total_border_radius ?? 0} min={0} max={40} onChange={(value) => patchStyle("order_total_border_radius", clampNumber(value, 0, 40))} />
                  <h3>Form appearance</h3>
                  <ColorSetting label="Background" value={style.form_background_color} onChange={(value) => patchStyle("form_background_color", value)} />
                  <ColorSetting label="Form border" value={style.form_border_color} onChange={(value) => patchStyle("form_border_color", value)} />
                  <NumberSetting label="Form border width" value={style.form_border_width} min={0} max={4} onChange={(value) => patchStyle("form_border_width", clampNumber(value, 0, 4))} />
                  <NumberSetting label="Form corner radius" value={style.form_border_radius} min={0} max={40} onChange={(value) => patchStyle("form_border_radius", clampNumber(value, 0, 40))} />
                  <h3>Default field style</h3>
                  <ColorSetting label="Input background" value={style.input_background_color} onChange={(value) => patchStyle("input_background_color", value)} />
                  <ColorSetting label="Input border" value={style.input_border_color} onChange={(value) => patchStyle("input_border_color", value)} />
                  <NumberSetting label="Input border width" value={style.input_border_width} min={0} max={4} onChange={(value) => patchStyle("input_border_width", clampNumber(value, 0, 4))} />
                  <ColorSetting label="Focus border" value={style.input_focus_border_color} onChange={(value) => patchStyle("input_focus_border_color", value)} />
                  <ColorSetting label="Input text" value={style.text_color} onChange={(value) => patchStyle("text_color", value)} />
                  <ColorSetting label="Label" value={style.label_color} onChange={(value) => patchStyle("label_color", value)} />
                  <ColorSetting label="Placeholder" value={style.placeholder_color} onChange={(value) => patchStyle("placeholder_color", value)} />
                  <ColorSetting label="Error state" value={style.input_error_color} onChange={(value) => patchStyle("input_error_color", value)} />
                  <NumberSetting label="Input height" value={style.input_height} min={40} max={80} onChange={(value) => patchStyle("input_height", clampNumber(value, 40, 80))} />
                  <NumberSetting label="Input corner radius" value={style.input_border_radius} min={0} max={30} onChange={(value) => patchStyle("input_border_radius", clampNumber(value, 0, 30))} />
                  <h3>Submit button</h3>
                  <ColorSetting label="Button pressed background" value={style.submit_button_active_background_color || "#374151"} onChange={(value) => patchStyle("submit_button_active_background_color", value)} />
                  <NumberSetting label="Button font size" value={style.submit_button_font_size ?? 16} min={12} max={40} onChange={(value) => patchStyle("submit_button_font_size", clampNumber(value, 12, 40))} />
                  <NumberSetting label="Button height" value={style.submit_button_height ?? 48} min={40} max={100} onChange={(value) => patchStyle("submit_button_height", clampNumber(value, 40, 100))} />
                  <NumberSetting label="Button corner radius" value={style.submit_button_border_radius ?? 8} min={0} max={40} onChange={(value) => patchStyle("submit_button_border_radius", clampNumber(value, 0, 40))} />
                  <ColorSetting label="Button background" value={style.submit_button_background_color} onChange={(value) => patchStyle("submit_button_background_color", value)} />
                  <ColorSetting label="Button text" value={style.submit_button_text_color} onChange={(value) => patchStyle("submit_button_text_color", value)} />
                  <Toggle label="Full-width button" checked={style.submit_button_full_width} onChange={(value) => patchStyle("submit_button_full_width", value)} />
                  {style.display_mode === "popup" ? (
                    <>
                      <h3>Popup appearance</h3>
                      <NumberSetting label="Popup maximum width" value={style.popup_max_width ?? 760} min={320} max={1600} onChange={(value) => patchStyle("popup_max_width", clampNumber(value, 320, 1600))} />
                      <NumberSetting label="Popup corner radius" value={style.popup_border_radius ?? 16} min={0} max={40} onChange={(value) => patchStyle("popup_border_radius", clampNumber(value, 0, 40))} />
                      <NumberSetting label="Overlay opacity" value={style.popup_overlay_opacity ?? 40} min={0} max={80} suffix="%" onChange={(value) => patchStyle("popup_overlay_opacity", clampNumber(value, 0, 80))} />
                      <p className="cfd-setting-help">Preview mode shows the popup size, overlay and corner radius with your form inside.</p>
                      <h3>Open-form button</h3>
                      <Toggle label="Full-width open-form button" checked={style.popup_button_full_width !== false} onChange={(value) => patchStyle("popup_button_full_width", value)} />
                      <ColorSetting label="Open-form button background" value={style.popup_button_background_color || "#111827"} onChange={(value) => patchStyle("popup_button_background_color", value)} />
                      <ColorSetting label="Open-form button text color" value={style.popup_button_text_color || "#ffffff"} onChange={(value) => patchStyle("popup_button_text_color", value)} />
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          </aside></InspectorPanel>
        ) : null}
      </div>

      {addOpen ? (
        <div className="cfd-modal-backdrop" role="presentation" onMouseDown={() => setAddOpen(false)}>
          <div className="cfd-add-dialog" role="dialog" aria-modal="true" aria-labelledby="cfd-add-title" onMouseDown={(event) => event.stopPropagation()}>
            <header><h2 id="cfd-add-title">Add a field or component</h2><button type="button" onClick={() => setAddOpen(false)}>×</button></header>
            <input className="cfd-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search fields" autoFocus />
            <h3>Customer information</h3>
            <div className="cfd-add-list">
              {hiddenFields.some((field) => field.type !== "component") ? hiddenFields.filter((field) => field.type !== "component").map((field) => (
                <div key={field.name}><i><CodFieldIcon name={field.icon_name || defaultFieldIconName(field.name)} /></i><span><strong>{field.label}</strong><small>{field.placeholder || `Collect the customer's ${field.label.toLowerCase()}`}</small></span><button type="button" onClick={() => addField(field)}>Add</button></div>
              )) : <p>All matching customer fields are already visible.</p>}
            </div>
            <h3>Order components</h3>
            <div className="cfd-add-list">
              {(settings.available_fields || []).filter((field) => field.name === "product_information" && (!search.trim() || field.label.toLowerCase().includes(search.trim().toLowerCase()))).map((field) => (
                <div key={field.name}><i>▧</i><span><strong>{field.label}</strong><small>Show the order's product image, title, variant, quantity and price.</small></span>{fieldMap.has(field.name) ? <em>Added</em> : <button type="button" aria-label="Add product information" onClick={() => addField(field)}>Add</button>}</div>
              ))}
              <div><i>▣</i><span><strong>Shipping method</strong><small>Let customers see and choose a COD shipping rate.</small></span>{style.show_shipping_method ? <em>Added</em> : <button type="button" onClick={() => { patchStyle("show_shipping_method", true); setAddOpen(false); }}>Add</button>}</div>
            </div>
            <div className="cfd-hidden-summary"><strong>Hidden fields and components</strong><span>{(settings.available_fields || []).filter((field) => !fieldMap.has(field.name)).length + (style.show_shipping_method ? 0 : 1)}</span></div>
            <button className="cfd-done" type="button" onClick={() => setAddOpen(false)}>Done</button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
